import {
  addDoc,
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  serverTimestamp,
  startAfter,
  where,
  type QueryDocumentSnapshot,
  type DocumentData,
} from 'firebase/firestore';
import { db } from '../firebase';

/** Page size for “Submitted feedback” + incremental load. */
export const FEEDBACK_PAGE_SIZE = 20;

export type FeedbackCategory = 'bug' | 'feature' | 'ui' | 'account' | 'other';

/** How the document was created — same `feedback` collection for both. */
export type FeedbackSource = 'feedback' | 'support_form';

export type SubmitFeedbackInput = {
  userId: string;
  userEmail: string;
  userRole: 'emt' | 'employer' | null;
  category: FeedbackCategory;
  title: string;
  message: string;
  pageArea: string;
};

export type SubmitSupportFormInput = {
  name: string;
  email: string;
  message: string;
};

/** Row returned to the client for “my feedback” (no other users’ documents). */
export type UserFeedbackItem = {
  id: string;
  title: string;
  message: string;
  category: FeedbackCategory;
  pageArea: string;
  status: string;
  createdAt: unknown;
};

function mapFeedbackDoc(docSnap: QueryDocumentSnapshot<DocumentData>): UserFeedbackItem {
  const data = docSnap.data();
  const cat = data.category;
  const category: FeedbackCategory =
    cat === 'bug' || cat === 'feature' || cat === 'ui' || cat === 'account' || cat === 'other'
      ? cat
      : 'other';
  return {
    id: docSnap.id,
    title: typeof data.title === 'string' ? data.title : '',
    message: typeof data.message === 'string' ? data.message : '',
    category,
    pageArea: typeof data.pageArea === 'string' ? data.pageArea : '',
    status: typeof data.status === 'string' ? data.status : 'new',
    createdAt: data.createdAt,
  };
}

export type FeedbackListPage = {
  items: UserFeedbackItem[];
  hasMore: boolean;
  endCursor: QueryDocumentSnapshot<DocumentData> | null;
};

/**
 * One page of the current user’s feedback only (`userId` equality + `createdAt` desc).
 */
export async function fetchFeedbackForUserPage(
  uid: string,
  pageSize: number,
  cursor?: QueryDocumentSnapshot<DocumentData> | null
): Promise<FeedbackListPage> {
  if (!db) {
    throw new Error('Database is not available. Please try again later.');
  }
  const base = collection(db, 'feedback');
  const q = cursor
    ? query(
        base,
        where('userId', '==', uid),
        orderBy('createdAt', 'desc'),
        startAfter(cursor),
        limit(pageSize + 1)
      )
    : query(base, where('userId', '==', uid), orderBy('createdAt', 'desc'), limit(pageSize + 1));
  const snap = await getDocs(q);
  const docs = snap.docs;
  const hasMore = docs.length > pageSize;
  const pageDocs = hasMore ? docs.slice(0, pageSize) : docs;
  const items = pageDocs.map((d) => mapFeedbackDoc(d));
  const endCursor = pageDocs.length > 0 ? pageDocs[pageDocs.length - 1]! : null;
  return { items, hasMore, endCursor };
}

export async function submitFeedback(input: SubmitFeedbackInput): Promise<void> {
  if (!db) {
    throw new Error('Database is not available. Please try again later.');
  }

  const payload: Record<string, unknown> = {
    source: 'feedback' satisfies FeedbackSource,
    userId: input.userId,
    userEmail: input.userEmail,
    category: input.category,
    title: input.title,
    message: input.message,
    pageArea: input.pageArea,
    status: 'new',
    createdAt: serverTimestamp(),
  };

  if (input.userRole === 'emt' || input.userRole === 'employer') {
    payload.userRole = input.userRole;
  }

  await addDoc(collection(db, 'feedback'), payload);
}

/**
 * Public contact/support form → same `feedback` collection as in-product Feedback,
 * tagged `source: "support_form"` so staff can tell them apart in Console.
 */
export async function submitSupportForm(input: SubmitSupportFormInput): Promise<void> {
  if (!db) {
    throw new Error('Database is not available. Please try again later.');
  }

  const name = input.name.trim();
  const email = input.email.trim();
  const message = input.message.trim();
  if (!name || !email || !message) {
    throw new Error('Name, email, and message are required.');
  }

  const title = `Support from ${name}`.slice(0, 280);

  await addDoc(collection(db, 'feedback'), {
    source: 'support_form' satisfies FeedbackSource,
    userId: 'anonymous',
    name,
    userEmail: email,
    category: 'other' as FeedbackCategory,
    title,
    message,
    pageArea: '/support',
    status: 'new',
    createdAt: serverTimestamp(),
  });
}
